import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import _get from 'lodash/get';
import {
  Department,
  FilterGroupingEnum,
  Intervention,
  InterventionScopeNameEnum,
  Task,
  TaskScopeNameEnum,
  Team,
  useInterventionsSearchListQuery,
  useTasksSearchListQuery,
  useTeamsSearchListQuery,
  User,
} from 'graphql-common';
import usePrevious from '@lib/hooks/usePrevious';
import useSearchParam from '@lib/hooks/useSearchParam';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import useTabParam from '@lib/hooks/useTabParam';
import useUsersQueryHook, {
  UsersCounters,
} from 'utils/fetch/hooks/useUsersQueryHook';
import useTeamsQueryHook from 'utils/fetch/hooks/useTeamsQueryHook';
import useDashboardTasksQueryHook from 'utils/fetch/hooks/useDashboardTasksQueryHook';
import useDepartmentQueryHook from 'utils/fetch/hooks/useDepartmentQueryHook';
import { ListQueryHookResult, SingleQueryHookResult } from 'utils/fetch/types';
import { DepartmentViewMode } from 'routes/Departments/enums';

export type UseDepartmentQueriesResult = {
  useDepartmentQueryHookResult: SingleQueryHookResult<Department>;
  useUsersQueryHookResult: ListQueryHookResult<User>;
  accessGroups: Team[];
  linkedTasks: Task[];
  linkedInterventions: Intervention[];
  useDepartmentMembersQueryHookResult: ListQueryHookResult<User> & {
    counters: UsersCounters;
  };
  useDepartmentAccessGroupsQueryHookResult: ListQueryHookResult<Team>;
  useDepartmentTasksQueryHookResult: ReturnType<
    typeof useDashboardTasksQueryHook
  >;
  taskTabParam: string;
  setTaskTabParam: (v: string) => void;
};

export default function useDepartmentQueries({
  viewMode,
}: {
  viewMode: DepartmentViewMode;
}): UseDepartmentQueriesResult {
  const { id = '' } = useParams();
  const isForm =
    viewMode === DepartmentViewMode.Add || viewMode === DepartmentViewMode.Edit;
  const skipLinkedData = !id || !isForm;

  const [searchQuery] = useSearchParam();
  const [paginationParams] = usePaginationParams();
  const [taskTabParam, setTaskTabParam] = useTabParam({
    allowedTabs: Object.values(TaskScopeNameEnum),
    defaultTab: TaskScopeNameEnum.Active,
  });

  const useDepartmentQueryHookResult = useDepartmentQueryHook({
    id,
    skip: viewMode === DepartmentViewMode.Add,
    fetchPolicy: 'cache-and-network',
  });

  // DepartmentContainer renders the same instance for the view and edit
  // routes (React reuses it across the tab switch, no remount), so
  // `cache-and-network`'s network leg only fires once, on the very first
  // mount. `path` is computed lazily/async on the BE, so that first fetch
  // (from viewing) can carry a stale/incomplete value — force a real refetch
  // when the user actually lands on the edit form, where accuracy matters
  // most (parent selection, resulting-hierarchy preview).
  const previousViewMode = usePrevious(viewMode);
  useEffect(() => {
    if (
      viewMode === DepartmentViewMode.Edit &&
      previousViewMode !== undefined &&
      previousViewMode !== DepartmentViewMode.Edit &&
      id
    ) {
      useDepartmentQueryHookResult.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, previousViewMode, id]);

  const useUsersQueryHookResult = useUsersQueryHook({
    skip: true,
    skipGetAllIds: skipLinkedData,
    additionalFilters: [
      {
        memberAssignedDepartmentsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: id },
        },
      },
    ],
  });

  const accessGroupsQuery = useTeamsSearchListQuery({
    fetchPolicy: 'cache-and-network',
    skip: skipLinkedData,
    variables: {
      limit: 100,
      filters: [
        {
          departmentsId: {
            grouping: FilterGroupingEnum.And,
            predicate: { eq: id },
          },
        },
      ],
    },
  });
  const accessGroups = _get(
    accessGroupsQuery.data || accessGroupsQuery.previousData,
    'data.collection',
    [],
  ) as Team[];

  const tasksQuery = useTasksSearchListQuery({
    fetchPolicy: 'cache-and-network',
    skip: skipLinkedData,
    variables: {
      limit: 100,
      scope: { name: TaskScopeNameEnum.Current },
      filters: [
        {
          departmentsId: {
            grouping: FilterGroupingEnum.And,
            predicate: { eq: id },
          },
        },
      ],
    },
  });
  const linkedTasksRaw = _get(
    tasksQuery.data || tasksQuery.previousData,
    'data.collection',
    [],
  ) as Task[];
  // A department↔task link applies to the whole recurring group, so dedupe
  // occurrences by groupKey before turning them into default field values.
  const seenGroupKeys = new Set<string>();
  const linkedTasks = linkedTasksRaw.filter((task) => {
    const key = task.groupKey ?? task.id;
    if (seenGroupKeys.has(key)) return false;
    seenGroupKeys.add(key);
    return true;
  });

  const interventionsQuery = useInterventionsSearchListQuery({
    fetchPolicy: 'cache-and-network',
    skip: skipLinkedData,
    variables: {
      limit: 100,
      scopeName: InterventionScopeNameEnum.All,
      filters: [
        {
          departmentsId: {
            grouping: FilterGroupingEnum.And,
            predicate: { eq: id },
          },
        },
      ],
    },
  });
  const linkedInterventions = _get(
    interventionsQuery.data || interventionsQuery.previousData,
    'data.collection',
    [],
  ) as Intervention[];

  const useDepartmentMembersQueryHookResult = useUsersQueryHook({
    paginationParams,
    searchQuery,
    includeShortData: true,
    includeFinalAccesses: true,
    includeTeamData: true,
    skip: viewMode !== DepartmentViewMode.ViewMembers,
    additionalFilters: [
      {
        memberAssignedDepartmentsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: id },
        },
      },
    ],
  });

  const useDepartmentAccessGroupsQueryHookResult = useTeamsQueryHook({
    paginationParams,
    orderingParams: paginationParams,
    searchQuery,
    skip: viewMode !== DepartmentViewMode.ViewAccessGroups,
    additionalFilters: [
      {
        departmentsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: id },
        },
      },
    ],
  });

  const useDepartmentTasksQueryHookResult = useDashboardTasksQueryHook({
    paginationParams,
    searchQuery,
    tabParam: taskTabParam,
    skip: viewMode !== DepartmentViewMode.ViewTasks,
    additionalFilters: [
      {
        departmentsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: id },
        },
      },
    ],
  });

  return {
    useDepartmentQueryHookResult,
    useUsersQueryHookResult,
    accessGroups,
    linkedTasks,
    linkedInterventions,
    useDepartmentMembersQueryHookResult,
    useDepartmentAccessGroupsQueryHookResult,
    useDepartmentTasksQueryHookResult,
    taskTabParam: taskTabParam || TaskScopeNameEnum.Active,
    setTaskTabParam,
  };
}
